// restRequestV3.js
import axios from 'axios';
import { Toast } from 'vant';
import router from '@/router';

// HTTP 状态码映射
const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_ERROR: 500
};

// 业务状态码映射
const BUSINESS_CODE = {
  SUCCESS: 200,
  TOKEN_EXPIRED: 401001,
  TOKEN_INVALID: 401002,
  NO_PERMISSION: 403001
};

// 免登录路径白名单 - 这些路径访问时不强制跳转登录页
const PUBLIC_PATHS = [
  '/s/',                // 短链接
  '/product/detail',    // 商品详情
  '/resource/view',     // 资源查看
  '/category/list',     // 分类列表
  '/booth',             // 展台
  '/AdvertisingBooth',  // 广告展台
  '/store',             // 店铺
  '/homeclone',         // 首页克隆
  '/salesDetails',      // 销售详情
  '/chipDetail',        // 芯片详情
  '/Login',             // 登录页本身
  '/Register',          // 注册页
  '/Privacy',           // 隐私协议
  '/Forget',            // 忘记密码
  '/nonet'              // 断网页
];

// 判断当前路径是否免登录
function isPublicPath() {
  const currentPath = router.currentRoute ? router.currentRoute.path : '';
  return PUBLIC_PATHS.some(function(path) {
    return currentPath.startsWith(path) || currentPath === path;
  });
}

// 获取基础配置
const getBaseConfig = function() {
  var config = {
    baseURL: process.env.VUE_APP_API_V3 || '', // 使用环境变量中的基础路径
    timeout: 15000, // 超时时间
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'AppTypeName': process.env.AppTypeName || '' // 添加AppTypeName
    }
  };

  // 如果存在store_id则添加到headers
  var currentStoreId = localStorage.getItem('currentStoreId');
  if (currentStoreId && parseInt(currentStoreId) > 0) {
    config.headers['Store-Id'] = currentStoreId;
  }

  console.log('------restRequestV3-----', config);
  return config;
};

// 创建 Axios 实例
const axiosInstance = axios.create(getBaseConfig());

// 请求计数器
let requestCount = 0;

// 显示加载提示
const showLoading = function(noIndicator) {
  if (noIndicator) return;
  requestCount++;
  if (requestCount === 1) {
    Toast.loading({
      message: '加载中...',
      forbidClick: true,
      duration: 0
    });
  }
};

// 隐藏加载提示
const hideLoading = function(noIndicator) {
  if (noIndicator) return;
  requestCount--;
  if (requestCount === 0) {
    Toast.clear();
  }
};

// 刷新 token
const refreshToken = function() {
  return new Promise(function(resolve, reject) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      reject(new Error('No refresh token'));
      return;
    }

    axios.post(process.env.VUE_APP_API_V3 + '/auth/refresh', { refreshToken })
      .then(function(response) {
        const { token, newRefreshToken } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        resolve(token);
      })
      .catch(function(error) {
        console.error('刷新token失败:', error);
        reject(error);
      });
  });
};

// 请求拦截器
axiosInstance.interceptors.request.use(
  function(config) {
    // 添加 token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    
    // 动态添加 Store-Id
    const currentStoreId = localStorage.getItem('currentStoreId');
    if (currentStoreId && parseInt(currentStoreId) > 0) {
      config.headers['Store-Id'] = currentStoreId;
    }

    // 防重复提交
    config.headers['formToken'] = new Date().getTime();

    // 添加租户ID
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['Tenant-Id'] = tenantId;
    }

    // 确保AppTypeName始终存在
    if (!config.headers['App-Type-Name']) {
      config.headers['App-Type-Name'] = process.env.AppTypeName || '';
    }

    // 显示加载提示
    showLoading(config.noIndicator);

    return config;
  },
  function(error) {
    hideLoading();
    return Promise.reject(error);
  }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  function(response) {
    hideLoading(response.config.noIndicator);

    const { code, data } = response.data;
    // 兼容后端返回 msg 或 message
    const msg = response.data.msg || response.data.message;

    // 处理业务状态码
    switch (code) {
      case BUSINESS_CODE.SUCCESS:
        return data;
      case 401:
        // Token 无效或过期，强制跳转登录页
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        if (!isPublicPath()) {
          Toast(msg || '登录已过期，请重新登录');
          router.push('/login');
        }
        throw new Error(msg || '登录已过期，请重新登录');
      case BUSINESS_CODE.TOKEN_EXPIRED:
        // Token 过期，尝试刷新
        return refreshToken().then(function(newToken) {
          if (newToken) {
            response.config.headers['Authorization'] = 'Bearer ' + newToken;
            return axiosInstance(response.config);
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            if (!isPublicPath()) {
              router.push('/login');
            }
            throw new Error('Token 已过期，请重新登录');
          }
        });
      case BUSINESS_CODE.TOKEN_INVALID:
      case BUSINESS_CODE.NO_PERMISSION:
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        if (!isPublicPath()) {
          router.push('/login');
        }
        throw new Error(msg || '请重新登录');
      default:
        throw new Error(msg || '请求失败');
    }
  },
  function(error) {
    hideLoading(error.config && error.config.noIndicator);

    // 处理 HTTP 错误
    if (error.response) {
      switch (error.response.status) {
        case HTTP_STATUS.UNAUTHORIZED:
          if (!isPublicPath()) {
            router.push('/login');
            Toast('请重新登录');
          }
          break;
        case HTTP_STATUS.FORBIDDEN:
          Toast('没有权限');
          break;
        case HTTP_STATUS.NOT_FOUND:
          Toast('请求的资源不存在');
          break;
        case HTTP_STATUS.INTERNAL_ERROR:
          Toast('服务器错误');
          break;
        default:
          Toast(error.response.data && error.response.data.msg || '请求失败');
      }
    } else if (error.request) {
      Toast('网络连接失败，请检查网络');
    } else {
      Toast('请求配置错误');
    }

    return Promise.reject(error);
  }
);

// RESTful 请求封装
const restRequestV3 = {
  // GET 请求
  get: function(url, params, noIndicator) {
    return axiosInstance.get(url, {
      params: params || {},
      noIndicator: noIndicator || false
    }).catch(function(error) {
      console.error('GET请求错误:', error);
      throw error;
    });
  },

  // POST 请求
  post: function(url, data, noIndicator, params) {
    const config = {
      noIndicator: noIndicator || false
    };
    
    // 如果有查询参数，添加到配置中
    if (params) {
      config.params = params;
    }
    
    return axiosInstance.post(url, data || {}, config).catch(function(error) {
      console.error('POST请求错误:', error);
      throw error;
    });
  },

  // PUT 请求
  put: function(url, data, config) {
    // 支持传递 config 对象，包含 params 和 noIndicator
    if (typeof config === 'boolean') {
      // 兼容旧的调用方式，第三个参数是 noIndicator
      config = { noIndicator: config };
    }
    
    const requestConfig = {
      noIndicator: (config && config.noIndicator) || false
    };
    
    // 如果有 params，添加到请求配置中
    if (config && config.params) {
      requestConfig.params = config.params;
    }
    
    return axiosInstance.put(url, data || {}, requestConfig).catch(function(error) {
      console.error('PUT请求错误:', error);
      throw error;
    });
  },

  // DELETE 请求
// DELETE 请求
  delete: function(url, params, noIndicator) {
    const isObject = typeof params === 'object' && params !== null;

    // 判断是否是对象类型来决定放到 params 还是 data
    const config = {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
      noIndicator: noIndicator || false
    };

    if (isObject) {
      config.data = params;
    } else {
      config.params = params || {};
    }

    return axiosInstance.delete(url, config).catch(function(error) {
      console.error('DELETE请求错误:', error);
      throw error;
    });
  },


  // 文件上传
  upload: function(url, fileName, file, onProgress, noIndicator) {
    var formData = new FormData();
    formData.append(fileName, file, file.name);

    return axiosInstance.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      noIndicator: noIndicator || false,
      onUploadProgress: function(progressEvent) {
        if (onProgress && typeof onProgress === 'function') {
          var percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(percent);
        }
      }
    }).catch(function(error) {
      console.error('文件上传错误:', error);
      throw error;
    });
  },

  // 文件下载
  download: function(url, params, filename, noIndicator) {
    return axiosInstance.get(url, {
      params: params || {},
      responseType: 'blob',
      noIndicator: noIndicator || false
    }).then(function(response) {
      // 创建下载链接
      var blob = new Blob([response.data]);
      var downloadElement = document.createElement('a');
      var href = window.URL.createObjectURL(blob);
      downloadElement.href = href;
      downloadElement.download = filename;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);
    }).catch(function(error) {
      console.error('文件下载错误:', error);
      throw error;
    });
  }
};

export default restRequestV3;



// WEBPACK FOOTER //
// ./src/utils/restRequestV3.js